import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

/** ~2 lines at spotlight caption font size (tunable). */
const MAX_CHARS_TWO_LINES = 120

const captionWord = PropTypes.shape({
  text: PropTypes.string.isRequired,
  start: PropTypes.number.isRequired,
  end: PropTypes.number.isRequired,
  speaker: PropTypes.string,
})

function normalizeWords(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return []
  return raw
    .map((w) => ({
      text: w?.text != null ? String(w.text) : '',
      start: Number(w.start),
      end: Number(w.end),
      speaker:
        w?.speaker != null && String(w.speaker).trim() !== ''
          ? String(w.speaker)
          : '',
    }))
    .filter(
      (w) => w.text.length > 0 && !Number.isNaN(w.start) && !Number.isNaN(w.end)
    )
}

/**
 * Partition words into static blocks: speaker change or ~2-line character budget.
 */
function buildChunks(words, maxChars) {
  if (!words.length) return []

  const chunks = []
  let chunkStart = 0
  let runLen = words[0].text.length

  for (let i = 1; i < words.length; i++) {
    const prev = words[i - 1]
    const curr = words[i]
    const spPrev = prev.speaker || ''
    const spCurr = curr.speaker || ''
    const candidateLen = runLen + 1 + curr.text.length

    if (spCurr !== spPrev || candidateLen > maxChars) {
      chunks.push({ startIdx: chunkStart, endIdx: i - 1 })
      chunkStart = i
      runLen = curr.text.length
    } else {
      runLen = candidateLen
    }
  }
  chunks.push({ startIdx: chunkStart, endIdx: words.length - 1 })
  return chunks
}

function findChunkForIndex(chunks, k) {
  if (k < 0 || !chunks.length) return null
  return chunks.find((c) => k >= c.startIdx && k <= c.endIdx) ?? null
}

/**
 * Last word index whose start time is <= tMs (for gap handling between words).
 */
function indexAtOrBeforeTime(words, tMs) {
  if (!words?.length) return -1
  let lo = 0
  let hi = words.length - 1
  let ans = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (words[mid].start <= tMs) {
      ans = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return ans
}

function strictlyActiveWordIndex(words, tMs) {
  const idx = indexAtOrBeforeTime(words, tMs)
  if (idx < 0) return -1
  const w = words[idx]
  if (tMs >= w.start && tMs < w.end) return idx
  return -1
}

function SpotlightVideoPlayer({
  videoSrc,
  captionsUrl,
  captionWords,
  videoAriaLabel,
  ariaDescribedBy,
  videoStyle,
}) {
  const words = useMemo(() => normalizeWords(captionWords), [captionWords])
  const showWordOverlay = words.length > 0

  const chunks = useMemo(() => buildChunks(words, MAX_CHARS_TWO_LINES), [words])

  const [timeMs, setTimeMs] = useState(0)

  const onTimeUpdate = useCallback((e) => {
    setTimeMs(Math.floor(e.target.currentTime * 1000))
  }, [])

  const onSeeked = useCallback((e) => {
    setTimeMs(Math.floor(e.target.currentTime * 1000))
  }, [])

  const lastStartedIdx = useMemo(
    () => indexAtOrBeforeTime(words, timeMs),
    [words, timeMs]
  )

  const chunk = useMemo(
    () => findChunkForIndex(chunks, lastStartedIdx),
    [chunks, lastStartedIdx]
  )

  const visible = useMemo(() => {
    if (!chunk || lastStartedIdx < 0) return []
    const out = []
    for (let i = chunk.startIdx; i <= lastStartedIdx; i++) {
      const w = words[i]
      if (w.start <= timeMs) {
        out.push({ word: w, globalIdx: i })
      }
    }
    return out
  }, [chunk, lastStartedIdx, words, timeMs])

  const activeIdx = useMemo(
    () => strictlyActiveWordIndex(words, timeMs),
    [words, timeMs]
  )

  return (
    <div className="spotlight-video-player-wrap">
      <video
        src={videoSrc}
        controls
        aria-label={videoAriaLabel}
        aria-describedby={ariaDescribedBy}
        style={videoStyle}
        onTimeUpdate={onTimeUpdate}
        onSeeked={onSeeked}
      >
        {captionsUrl ? (
          <track
            kind="captions"
            src={captionsUrl}
            srcLang="en"
            label="English"
            default
          />
        ) : null}
      </video>
      {showWordOverlay ? (
        <div className="spotlight-video-word-captions" aria-hidden="true">
          {visible.map(({ word: w, globalIdx }, displayIndex) => {
            const isActive = globalIdx === activeIdx && activeIdx >= 0
            const showSpaceAfter = displayIndex < visible.length - 1
            return (
              <span
                key={`${globalIdx}-${w.start}-${w.text}`}
                className={
                  isActive
                    ? 'spotlight-video-word spotlight-video-word--active'
                    : 'spotlight-video-word'
                }
              >
                {w.text}
                {showSpaceAfter ? ' ' : ''}
              </span>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

SpotlightVideoPlayer.propTypes = {
  videoSrc: PropTypes.string.isRequired,
  captionsUrl: PropTypes.string,
  captionWords: PropTypes.arrayOf(captionWord),
  videoAriaLabel: PropTypes.string.isRequired,
  ariaDescribedBy: PropTypes.string,
  videoStyle: PropTypes.object,
}

SpotlightVideoPlayer.defaultProps = {
  captionsUrl: '',
  captionWords: null,
  ariaDescribedBy: undefined,
  videoStyle: undefined,
}

export default SpotlightVideoPlayer
